import { useDispatch, useSelector } from 'react-redux';
import { closeModal, openAuthorizationModal, setIsErrorGetOffersListing, setSaveOfferResult } from '../redux';
import { redirect } from '../utils';
import {
  selectFormDefaults,
  selectRegionId,
  selectRealEstateType,
  selectEntityId,
  selectProgramsVersion,
} from '../selectors/calculator';
import { useGetOffers } from './useGetOffers';
import { mortgageMiniCalculatorApi } from '../api';
import { useApplicationContext } from 'shared/common/utils/applicationContext';
import { ETypeAction, TObjectType, TRealEstateType } from '../types';
import { ISaveOffersFromListingV1Response } from 'shared/common/repositories/mortgage-broker-calculator/entities/save_offers_from_listing/SaveOffersFromListingV1Response';
import { ERuntimeSettings } from 'shared/common/types/runtimeSettings';

interface IUseSaveOffersOptions {
  redirectUrl?: string;
  defaultRedirectUrl: string;
}

interface IUseSaveOffersResult {
  isLoading: boolean;
  saveOffers: (typeAction: ETypeAction) => Promise<void>;
  shouldUseAuthActionV3: boolean;
}

export const useSaveOffers = (options: IUseSaveOffersOptions): IUseSaveOffersResult => {
  const { redirectUrl, defaultRedirectUrl } = options;
  const { logger, config } = useApplicationContext();
  const dispatch = useDispatch();

  const isEnableGetOffersV3 = config.getStrict(ERuntimeSettings.enableGetOffersV3);
  const entityId = useSelector(selectEntityId);
  const formData = useSelector(selectFormDefaults);
  const regionId = useSelector(selectRegionId);
  const realEstateType = useSelector(selectRealEstateType);
  const programsVersion = useSelector(selectProgramsVersion);

  const { refetch: refetchOffers } = useGetOffers();
  const [saveOffersFromListing, { isLoading }] =
    mortgageMiniCalculatorApi.endpoints.saveOffersFromListing.useMutation();

  const saveOffersResultToRedux = (result: ISaveOffersFromListingV1Response) => {
    dispatch(
      setSaveOfferResult({
        applicationId: result.applicationId,
        redirectUrl: result.redirectUrl,
      }),
    );
  };

  const handleInvalidProgramsVersionError = async (typeAction: ETypeAction) => {
    try {
      // Получаем новый programsVersion
      await refetchOffers();
    } catch (retryError) {
      logger.error(retryError, {
        domain: 'useSaveOffers',
        description: `Ошибка при получении офферов после saveOffersFromListing (${typeAction})`,
      });
    }
  };

  const saveOffers = async (typeAction: ETypeAction) => {
    const params = {
      programsVersion,
      searchParams: {
        regionId,
        realEstate: {
          type: realEstateType as TRealEstateType,
          id: realEstateType === 'newBuilding' ? entityId : 0,
        },
        objectType: formData.objectType as TObjectType,
        objectCost: formData.objectCost,
        downPayment: formData.downPayment,
        loanTermYears: formData.loanTermYears,
      },
    };

    try {
      const result = await saveOffersFromListing(params).unwrap();

      if (typeAction === ETypeAction.AUTH_AND_REDIRECT) {
        dispatch(openAuthorizationModal());
        saveOffersResultToRedux(result);
      } else {
        redirect(result?.redirectUrl || redirectUrl || defaultRedirectUrl);
      }
    } catch (error) {
      logger.error(error, {
        domain: 'useSaveOffers',
        description: `Ошибка при сохранение офферов (${typeAction})`,
      });

      if (error.message === 'Invalid programs version') {
        await handleInvalidProgramsVersionError(typeAction);
      }

      dispatch(closeModal());
      dispatch(setIsErrorGetOffersListing(true));
    }
  };

  return {
    isLoading,
    saveOffers,
    shouldUseAuthActionV3: Boolean(isEnableGetOffersV3 && programsVersion && realEstateType),
  };
};
